import * as React from "react";
import type { GetServerSideProps } from "next";

import NextPageWithLayout from "@/src/components/Layout/NextPageWithLayout";
import { NextPageWithLayoutProps, PageProps } from "@/src/types";
import Layout from "@/src/components/Layout/GridLayout";
import Profile from "@/src/routes/profile/routes/[handle]";

import { dehydrate, QueryClient } from "@tanstack/react-query";
import {
  prefetchUserNFTs,
  prefetchUserProfile,
} from "@/src/routes/profile/routes/[handle]/services";
import ReactQueryErrorFallback from "@/src/components/ReactQueryErrorBoundary";
import ProfileErrorBoundary from "@/src/routes/profile/routes/[handle]/components/ProfileErrorBoundary";
import { prefetchUserAchievementData } from "@/src/routes/profile/routes/[handle]/routes/achievements/services";
import { prefetchGlobalNavHeadersData } from "@/src/services/api/prefetch";
import { fetchOnboardingData } from "@/app/lib/services/serverSideCalls";

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const queryClient = new QueryClient();

  const query = ctx.query;

  const config = {
    headers: { Cookie: ctx.req.headers.cookie ?? "" },
  };

  const swooshHandleForPP = query.handle as string;

  await prefetchGlobalNavHeadersData(queryClient);
  await fetchOnboardingData(config, queryClient);
  await prefetchUserNFTs(queryClient, swooshHandleForPP);
  await prefetchUserProfile(queryClient, swooshHandleForPP);
  await prefetchUserAchievementData(queryClient, swooshHandleForPP);

  return {
    props: {
      ...query,
      dehydratedState: dehydrate(queryClient), // hydrates the data via the _.app so that the data we (pre)fetched is usable
      profileHandle: swooshHandleForPP,
    },
  };
};

const Account: NextPageWithLayoutProps<
  PageProps & { profileHandle: string }
> = ({ profileHandle }) => {
  return (
    <ReactQueryErrorFallback FallbackComponent={ProfileErrorBoundary}>
      <Profile swooshHandle={profileHandle} />
    </ReactQueryErrorFallback>
  );
};

export default NextPageWithLayout({ Page: Account, Layout });
